import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Loader2, UserPlus, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WalkInDialog = ({ slot, date, prefill, onClose, onCreated }) => {
  const { t } = useTranslation();
  const token = localStorage.getItem('sessionToken');
  const [options, setOptions] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    service_id: prefill?.service_id || '', staff_id: slot.staffId || prefill?.staff_id || '',
    appointment_time: slot.time,
    customer_name: prefill?.customer_name || '', customer_phone: prefill?.customer_phone || '',
    customer_email: prefill?.customer_email || '', notes: '', price: '',
  });
  const [addOnIds, setAddOnIds] = useState([]);

  useEffect(() => {
    axios.get(`${API}/provider/walk-in/options`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setOptions(res.data);
        setForm((f) => ({ ...f, service_id: f.service_id || res.data.services[0]?.id || '' }));
      })
      .catch(() => setError(t('walkIn.optionsFailed', 'Could not load your services')));
  }, [token, t]);

  const service = options?.services?.find((s) => s.id === form.service_id);
  const currentStaffName = options?.staff?.find((s) => s.id === form.staff_id)?.name || slot.staffName;
  const chosenAddOns = (options?.addons || []).filter((a) => addOnIds.includes(a.id));
  const addOnsTotal = Number(chosenAddOns.reduce((sum, a) => sum + Number(a.price || 0), 0).toFixed(3));
  const billTotal = Number(
    ((form.price === '' ? Number(service?.price || 0) : Number(form.price || 0)) + addOnsTotal).toFixed(3)
  );

  const submit = async () => {
    setError('');
    if (!form.service_id || !form.customer_name.trim() || !form.customer_phone.trim() || !form.customer_email.trim()) {
      setError(t('walkIn.missingFields', 'Service, name, phone and email are all needed'));
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/provider/walk-in`, {
        ...form,
        appointment_date: date,
        price: form.price === '' ? undefined : Number(form.price),
        staff_id: form.staff_id || null,
        addon_ids: addOnIds,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onCreated(res.data.appointment);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.error === 'slot_taken') {
        setError(t('walkIn.slotTaken', 'That slot is already booked for this staff member'));
      } else if (detail?.error === 'staff_unavailable') {
        setError(detail.reason === 'outside_hours'
          ? t('walkIn.outsideHours', 'That is outside the opening hours')
          : t('walkIn.staffUnavailable', 'This staff member is not available then'));
      } else {
        setError(typeof detail === 'string' ? detail : t('walkIn.saveFailed', 'Could not add the walk-in'));
      }
    } finally {
      setSaving(false);
    }
  };

  const field = 'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-0 sm:items-center sm:p-4" data-testid="walk-in-dialog">
      <div className="max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl sm:max-w-lg sm:rounded-2xl">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-amber-600">
              {t('walkIn.badge', 'Walk-in')}
            </p>
            <h3 className="text-lg font-bold text-slate-900">
              {prefill ? t('walkIn.repeatTitle', 'Book again') : t('walkIn.title', 'Add a walk-in')}
            </h3>
            <p className="numeric-ltr text-xs text-slate-500">
              {date} · {form.appointment_time}{currentStaffName ? ` · ${currentStaffName}` : ''}
            </p>
          </div>
          <button onClick={onClose} data-testid="walk-in-close" className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mb-4 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700" data-testid="walk-in-commission-note">
          {t('walkIn.noCommission', 'Walk-ins are yours: no EnayaHub commission, tracked on your walk-in earnings page.')}
        </p>

        {error && (
          <p className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700" data-testid="walk-in-error">{error}</p>
        )}

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-bold text-slate-600">{t('walkIn.service', 'Service')} *</label>
            <select
              className={field}
              value={form.service_id}
              onChange={(e) => setForm({ ...form, service_id: e.target.value })}
              data-testid="walk-in-service"
            >
              {(options?.services || []).map((s) => (
                <option key={s.id} value={s.id}>{s.name} · {s.duration}m · {s.price} {t('common.currency', 'KWD')}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-bold text-slate-600">{t('walkIn.staff', 'Staff member')}</label>
              <select
                className={field}
                value={form.staff_id}
                onChange={(e) => setForm({ ...form, staff_id: e.target.value })}
                data-testid="walk-in-staff"
              >
                <option value="">{t('walkIn.anyStaff', 'Not assigned')}</option>
                {(options?.staff || []).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-slate-600">{t('walkIn.time', 'Time')} *</label>
              <input
                type="time"
                className={`${field} numeric-ltr`}
                value={form.appointment_time}
                onChange={(e) => setForm({ ...form, appointment_time: e.target.value })}
                data-testid="walk-in-time"
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold text-slate-600">{t('walkIn.customerName', 'Customer name')} *</label>
            <input className={field} value={form.customer_name} data-testid="walk-in-name"
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-bold text-slate-600">{t('walkIn.phone', 'Phone')} *</label>
              <input className={`${field} numeric-ltr`} value={form.customer_phone} data-testid="walk-in-phone"
                placeholder="+965..." onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-slate-600">{t('walkIn.email', 'Email')} *</label>
              <input className={field} type="email" value={form.customer_email} data-testid="walk-in-email"
                onChange={(e) => setForm({ ...form, customer_email: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-bold text-slate-600">{t('walkIn.price', 'Price charged')}</label>
              <input className={`${field} numeric-ltr`} type="number" step="0.25" data-testid="walk-in-price"
                placeholder={service ? String(service.price) : '0'}
                value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold text-slate-600">{t('walkIn.notes', 'Notes')}</label>
              <input className={field} value={form.notes} data-testid="walk-in-notes"
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
        </div>

        {(options?.addons || []).length > 0 && (
          <div className="mt-4 rounded-xl bg-amber-50 p-3" data-testid="walk-in-addons">
            <p className="mb-2 text-xs font-bold text-amber-800">
              {t('addOns.walkInTitle', 'Extras (no extra time)')}
            </p>
            <div className="grid gap-1.5 sm:grid-cols-2">
              {(options?.addons || []).map((addon) => (
                <label key={addon.id} className="flex cursor-pointer items-center gap-2 text-xs font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={addOnIds.includes(addon.id)}
                    data-testid={`walk-in-addon-${addon.id}`}
                    onChange={() => setAddOnIds((ids) => (
                      ids.includes(addon.id) ? ids.filter((x) => x !== addon.id) : [...ids, addon.id]
                    ))}
                  />
                  <span className="flex-1">{addon.name}</span>
                  <span className="numeric-ltr text-amber-700">+{Number(addon.price).toFixed(3)}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {billTotal > 0 && (
          <p className="numeric-ltr mt-3 text-end text-sm font-bold text-slate-900" data-testid="walk-in-total">
            {t('addOns.billTotal', 'Bill total')}: {billTotal.toFixed(3)} {t('common.currency', 'KWD')}
          </p>
        )}

        <button
          onClick={submit}
          disabled={saving}
          data-testid="walk-in-submit"
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
          {prefill ? t('walkIn.bookAgain', 'Book again') : t('walkIn.submit', 'Add walk-in')}
        </button>
      </div>
    </div>
  );
};

export default WalkInDialog;
